import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useWatchAuth } from '@components/auth/hooks';
import PropTypes from 'prop-types';
import { LinearProgress } from '@mui';
import { authLogout, setAgencySuccess } from '@actions/auth-actions';
import { fetchDataTypes } from '@actions/data-types-actions';
import { pollUnreadNotificationsAsync } from '@actions/notifications-actions';
import { paper } from '@constants/mui-theme';
import { dataTypesLoaderSelector } from '@selectors/data-types-selector';
import Header from '@components/header';
import { getAgencyId } from '@utils/permission-utils';
import ActionConfirmationDialog from './confirmation-dialog';
import ErrorSnackbar from './error-snackbar';
import InfoSnackbar from './info-snackbar';
import useIdleMonitor from './idle-monitor';
import Disclaimer from './disclaimer';
import AgencySelect from './agency-select';
import WarningDialog from './warning-dialog';
import SomFooter from '@shared/som-footer';
import { getConfig } from '@components/config/selectors';
import { getConfigKey } from '@utils/config-utils';
import './authenticated.scss';

// Module-scoped (not component state): routes.js mounts a fresh AuthenticatedLayout instance
// per route rather than one persistent layout, so a useRef here would reset - and look like
// "first render" - on every navigation. This only needs to be true once, for the app's actual
// first page load, which only happens once per page load.
let hasRenderedOnce = false;

const AuthenticatedLayout = ({ children }) => {
  const dispatch = useDispatch();
  const loader = useSelector(dataTypesLoaderSelector);
  const {userLoaded} = useSelector(getConfig);
  const location = useLocation();

  // Move focus to the new page's heading on every route change, so screen readers announce
  // it instead of staying wherever focus was left on the previous page - and reuse that same
  // heading text for the tab title, so it also reflects whatever page is actually shown.
  useEffect(() => {
    if (!hasRenderedOnce) {
      hasRenderedOnce = true;
      return;
    }
    // Exclude headings inside nav/aside landmarks (e.g. the sidebar menu's own hidden
    // heading) - those don't change between pages, so they'd always win a plain querySelector
    // and we'd never reach the actual page content's heading.
    const headings = document.querySelectorAll('#main-content h1, #main-content h2, #main-content h3');
    const heading = Array.from(headings).find(candidate => !candidate.closest('nav, aside'));
    if (heading) {
      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1');
      }
      heading.focus();
      document.title = `${heading.textContent} - Coordinate`;
    }
  }, [location.pathname]);

  // Handle idle logout
  const onIdle = useCallback(() => {
    dispatch(authLogout());
  }, [dispatch]);

  // Use idle monitor hook
  useIdleMonitor(onIdle);

  // Initialize on mount
  useEffect(() => {
    dispatch(fetchDataTypes());
    dispatch(setAgencySuccess(getAgencyId()));
    window.stopPolling = false;
    dispatch(pollUnreadNotificationsAsync());

    // Cleanup on unmount
    return () => {
      window.stopPolling = true;
    };
  }, [dispatch]);

  useWatchAuth();

  const { count, total } = loader;
  const loaded = count === total && userLoaded;
  const somFooterEnabled = getConfigKey('somDigitalStandards')?.footer?.enabled;
  const progress = useMemo(() => {
    if (userLoaded) {
      return 100 * (count + 1) / (total + 1);
    }
    return 100 * (count) / (total + 1);
  }, [count, total, userLoaded]);

  return (
    <div styleName="app-container">
      <nav aria-label="Skip navigation">
        <a href="#main-content" styleName="skip-nav">
          Skip to main content
        </a>
      </nav>
      <header
        styleName="header-container"
        style={{
          ...paper,
          boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px'
        }}
      >
        <Header />
      </header>
      <main
        id="main-content"
        styleName={`content-container${somFooterEnabled ? ' content-container-with-footer' : ''}`}
      >
        {loaded
          ? children
          : <LinearProgress variant="determinate" value={progress} />}
      </main>
      <InfoSnackbar />
      <ErrorSnackbar />
      <ActionConfirmationDialog />
      <WarningDialog />
      <AgencySelect />
      <Disclaimer />
      <SomFooter />
    </div>
  );
};

AuthenticatedLayout.propTypes = {
  children: PropTypes.element
};

export default AuthenticatedLayout;
